/**
 * @fileoverview OptimizationEventService - SSE-based real-time optimization updates
 * Replaces polling with Server-Sent Events for better user experience
 */

const API_BASE_URL = (() => {
  const envBase = import.meta.env?.VITE_API_URL as string | undefined;
  if (envBase && typeof envBase === 'string' && envBase.trim().length > 0) {
    return envBase.replace(/\/$/, '');
  }
  return '/api';
})();

const buildEndpoint = (path: string): string => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE_URL}${normalizedPath}`;
};

import type { OptimizationResult, BacktestStats } from '../../types/store';

export interface OptimizationJob {
  id: string;
  userId: string;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
  jobType: 'single' | 'batch' | 'walk-forward' | 'optimization';
  priority: number;
  parameters: {
    symbol: string;
    interval: string;
    strategy: string;
    portfolioAmount: number;
    portfolioCurrency: string;
    startDate?: string;
    endDate?: string;
    optimizationTarget?: string;
    parameterRanges?: Record<string, { min: number; max: number; step: number }>;
    walkForward?: unknown;
  };
  progress?: number;
  result?: unknown;
  error?: string;
  estimatedDuration?: number;
  actualDuration?: number;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
  currentCombination?: number;
  totalCombinations?: number;
  bestResult?: OptimizationResult;
  allResults?: Array<{
    parameters: Record<string, number>;
    metrics: Record<string, number>;
    score: number;
  }>;
  // Top-level properties returned by backend (duplicated from parameters for convenience)
  parameterRanges?: Record<string, { min: number; max: number; step: number; current?: number[] }>;
  optimizationTarget?: string;
}

export interface OptimizationProgress {
  current: number;
  total: number;
  progress: number;
  completed: boolean;
  bestSoFar?: {
    parameters: Record<string, number>;
    score: number;
    metrics: BacktestStats;
  };
  currentResult?: OptimizationResult;
  estimatedTimeRemaining?: number;
}

export type OptimizationEventType = 'progress' | 'completed' | 'failed' | 'cancelled' | 'queued' | 'heartbeat' | 'connected';

export interface OptimizationEvent {
  type: OptimizationEventType;
  jobId: string;
  data: OptimizationProgress | OptimizationJob | { 
    error: string;
  } | {
    parameters: Record<string, number>;
    score: number;
    metrics: BacktestStats;
    total?: number;
  };
}

export class OptimizationEventService {
  private eventSource: EventSource | null = null;
  private subscribers = new Map<string, Set<(event: OptimizationEvent) => void>>();
  private globalSubscribers = new Set<(event: OptimizationEvent) => void>();
  private connectionRetryCount = 0;
  private maxRetries = 5;
  private retryDelay = 1000;
  private isConnected = false;
  private retryTimeoutId: NodeJS.Timeout | null = null;

  /**
   * Start SSE connection for optimization events
   */
  connect(): void {
    if (this.eventSource &&
        (this.eventSource.readyState === EventSource.OPEN ||
         this.eventSource.readyState === EventSource.CONNECTING)) {
      console.log('[OptimizationEventService] Already connected or connecting');
      return;
    }

    if (this.eventSource) {
      this.disconnect();
    }
    
    try {
      // Use the existing SSE endpoint that supports job streaming
      const eventSourceUrl = buildEndpoint('/optimize/events');
      this.eventSource = new EventSource(eventSourceUrl, { withCredentials: true });
      
      this.eventSource.onopen = () => {
        this.isConnected = true;
        this.connectionRetryCount = 0;
      };

      this.eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Skip connection confirmation and heartbeat events
          if (data.type === 'connected' || data.type === 'heartbeat') {
            return;
          }
          
          // Normalize event type by stripping 'optimization:' prefix if present
          const normalizedType = data.type?.replace(/^optimization:/, '') || data.type;
          
          this.handleEvent({ ...data, type: normalizedType } as OptimizationEvent);
        } catch (error) {
          console.error('[OptimizationEventService] Error parsing SSE message:', error);
        }
      };

      this.eventSource.onerror = (error) => {
        console.error('[OptimizationEventService] SSE connection error:', error);
        this.isConnected = false;
        this.handleConnectionError();
      };

    } catch (error) {
      console.error('[OptimizationEventService] Error creating EventSource:', error);
      this.handleConnectionError();
    }
  }

  /**
   * Disconnect from SSE
   */
  disconnect(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
      this.isConnected = false;
    }
    
    // Clear any pending retry attempts
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }
    
    // Reset retry count
    this.connectionRetryCount = 0;
  }

  /**
   * Subscribe to optimization events for a specific job
   */
  subscribe(jobId: string, callback: (event: OptimizationEvent) => void): () => void {
    if (!this.subscribers.has(jobId)) {
      this.subscribers.set(jobId, new Set());
    }
    
    this.subscribers.get(jobId)!.add(callback);
    
    // Auto-connect if not already connected
    if (!this.isConnected && !this.eventSource) {
      this.connect();
    }

    // Return unsubscribe function
    return () => {
      const jobSubscribers = this.subscribers.get(jobId);
      if (jobSubscribers) {
        jobSubscribers.delete(callback);
        if (jobSubscribers.size === 0) {
          this.subscribers.delete(jobId);
        }
      }
      
      // Disconnect if no more subscribers
      if (this.subscribers.size === 0 && this.globalSubscribers.size === 0) {
        this.disconnect();
      }
    };
  }

  /**
   * Subscribe to all optimization events globally
   */
  subscribeGlobal(callback: (event: OptimizationEvent) => void): () => void {
    this.globalSubscribers.add(callback);
    
    // Auto-connect if not already connected
    if (!this.isConnected && !this.eventSource) {
      this.connect();
    }

    // Return unsubscribe function
    return () => {
      this.globalSubscribers.delete(callback);
      
      // Disconnect if no more subscribers
      if (this.subscribers.size === 0 && this.globalSubscribers.size === 0) {
        this.disconnect();
      }
    };
  }

  /**
   * Get current connection status
   */
  isConnectedToSSE(): boolean {
    return this.isConnected && this.eventSource?.readyState === EventSource.OPEN;
  }

  /**
   * Handle incoming SSE events
   */
  private handleEvent(event: OptimizationEvent): void {
    // Notify job-specific subscribers
    const jobSubscribers = this.subscribers.get(event.jobId);
    if (jobSubscribers) {
      jobSubscribers.forEach(callback => {
        try {
          callback(event);
        } catch (error) {
        }
      });
    }
    
    // Also notify global subscribers (for completion events, etc.)
    this.globalSubscribers.forEach((callback: (event: OptimizationEvent) => void) => {
      try {
        callback(event);
      } catch (error) {
      }
    });
  }

  /**
   * Handle connection errors with retry logic
   */
  private handleConnectionError(): void {
    if (this.connectionRetryCount < this.maxRetries && this.subscribers.size > 0) {
      this.connectionRetryCount++;
      const delay = this.retryDelay * Math.pow(2, this.connectionRetryCount - 1); // Exponential backoff
      
      // Clear any existing retry timeout
      if (this.retryTimeoutId) {
        clearTimeout(this.retryTimeoutId);
      }
      
      this.retryTimeoutId = setTimeout(() => {
        this.retryTimeoutId = null;
        this.connect();
      }, delay);
    } else {
    }
  }

  /**
   * Fallback to polling for specific job (if SSE fails)
   */
  async fallbackToPoll(jobId: string, onProgress: (progress: OptimizationProgress) => void): Promise<void> {
    const poll = async () => {
      try {
        const response = await fetch(buildEndpoint(`/optimize/progress/${jobId}`), {
          credentials: 'include'
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        if (data.success && data.data) {
          const progressData = data.data;
          const current = progressData.current || 0;
          const total = progressData.total || 1;
          const progress: OptimizationProgress = {
            current,
            total,
            progress: total > 0 ? (current / total) * 100 : 0,
            completed: progressData.completed || false,
            bestSoFar: progressData.bestSoFar,
            currentResult: progressData.currentResult
          };
          
          onProgress(progress);
          
          if (!progress.completed) {
            setTimeout(poll, 5000); // Poll every 5 seconds
          }
        }
      } catch (error) {
        setTimeout(poll, 10000); // Retry after 10 seconds on error
      }
    };
    
    poll();
  }
}

// Singleton instance
export const optimizationEventService = new OptimizationEventService();